import React, { useState, useEffect } from 'react';
import residencesService from '../../../services/carehome_manager/residencesService';
import { formatDateShort } from '../../../utils/dateUtils';
import Loader from '../../../components/Loader';
import { TabSkeleton } from '../../../skeletons';
import Modal from '../../../components/Modal';
import DocumentViewer from '../../../components/DocumentViewer';

// Stored file paths are relative to the API origin, not to /api.
const FILE_ORIGIN = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const fileUrl = (stored) => (/^https?:\/\//i.test(stored) ? stored : `${FILE_ORIGIN}${stored}`);

/**
 * Individual Row Component to manage its own 'Read More' state correctly
 */
const CareNoteRow = ({ note }) => {
    const [isDescExpanded, setIsDescExpanded] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [photoView, setPhotoView] = useState(null); // { url, name, title, kind } for DocumentViewer

    // Description Truncation
    const shouldTruncateDesc = note.description.length > 100;
    const displayedDescription = isDescExpanded || !shouldTruncateDesc 
        ? note.description 
        : `${note.description.substring(0, 100)}...`;

    // Every action the carer recorded, with the questions they answered.
    // Older notes have no entries — they fall back to the summary strings.
    const entries = Array.isArray(note.entries) ? note.entries : [];
    const hasEntries = entries.length > 0;

    // Types Truncation (Main View)
    const maxVisibleTypes = 2;
    const rowItems = hasEntries ? entries : note.types;
    const shouldShowModalTrigger = rowItems.length > maxVisibleTypes;
    const displayedTypes = rowItems.slice(0, maxVisibleTypes);

    return (
        <>
            <div
                className="grid items-center px-6 py-4 bg-white dark:bg-[#262b33] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 gap-4 hover:shadow-md transition-shadow"
                style={{ gridTemplateColumns: '1.2fr 1fr 2.5fr 1.5fr 1fr 1fr' }}
            >
                {/* Staff Name */}
                <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">
                    {note.staffName}
                </div>

                {/* Date */}
                <div className="text-[13px] text-gray-600 dark:text-gray-400">
                    {formatDateShort(note.date)}
                </div>

                {/* Description */}
                <div className="text-[12px] text-gray-600 dark:text-gray-300 leading-snug pr-4">
                    {displayedDescription}{' '}
                    {shouldTruncateDesc && (
                        <span 
                            onClick={() => setIsDescExpanded(!isDescExpanded)}
                            className="text-blue-600 dark:text-blue-400 font-medium cursor-pointer hover:underline"
                        >
                            {isDescExpanded ? 'Read Less' : 'Read More...'}
                        </span>
                    )}
                </div>

                {/* Type (Stacked Badges) */}
                <div className="flex flex-col items-start gap-1.5">
                    {displayedTypes.map((item, index) => (
                        <span
                            key={index}
                            className="text-[10px] font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 px-2 py-0.5 rounded"
                        >
                            {hasEntries
                                ? `${item.label}${item.summary ? ` — ${item.summary}` : ''}`
                                : item}
                            {hasEntries && Array.isArray(item.photos) && item.photos.length > 0 && (
                                <span className="ml-1" title={`${item.photos.length} photo${item.photos.length > 1 ? 's' : ''}`}>
                                    📷
                                </span>
                            )}
                        </span>
                    ))}
                    {(shouldShowModalTrigger || hasEntries) && (
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:underline mt-0.5"
                        >
                            {shouldShowModalTrigger
                                ? `+${rowItems.length - maxVisibleTypes} More`
                                : 'View details'}
                        </button>
                    )}
                </div>

                {/* Priority */}
                <div className="text-[13px] text-gray-800 dark:text-gray-200 font-medium">
                    {note.priority}
                </div>

                {/* Emotion */}
                <div className="flex flex-col items-center justify-center">
                    <span className="text-2xl leading-none">{note.emotion.emoji}</span>
                    <span className="text-[11px] font-medium text-gray-700 dark:text-gray-400 mt-1">{note.emotion.label}</span>
                </div>
            </div>

            {/* Everything the carer recorded: each action, and every question
                they answered underneath it. */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="What was recorded"
                maxWidth={hasEntries ? 'max-w-lg' : 'max-w-sm'}
            >
                {hasEntries ? (
                    <div className="flex flex-col gap-3 max-h-[65vh] overflow-y-auto pr-1">
                        {entries.map((entry, index) => (
                            <div
                                key={index}
                                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#1f232a] px-4 py-3"
                            >
                                <div className="flex items-baseline justify-between gap-3">
                                    <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">
                                        {entry.label}
                                    </span>
                                    {entry.categoryName && (
                                        <span className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                            {entry.categoryName}
                                        </span>
                                    )}
                                </div>

                                {entry.summary && (
                                    <div className="text-[12px] font-medium text-blue-600 dark:text-blue-400 mt-0.5">
                                        {entry.summary}
                                    </div>
                                )}

                                {Array.isArray(entry.photos) && entry.photos.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {entry.photos.map((photo, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => setPhotoView({
                                                    url: fileUrl(photo.url),
                                                    name: String(photo.url).split('/').pop(),
                                                    title: photo.caption || `${entry.label} photo ${i + 1}`,
                                                    kind: 'image',
                                                })}
                                                title={photo.caption || 'View full size'}
                                                className="block w-16 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-blue-400 transition"
                                            >
                                                <img
                                                    src={fileUrl(photo.url)}
                                                    alt={photo.caption || `${entry.label} photo ${i + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {Array.isArray(entry.details) && entry.details.length > 0 && (
                                    <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                                        {entry.details.map((detail, i) => (
                                            <React.Fragment key={i}>
                                                <dt className="text-[11px] text-gray-500 dark:text-gray-400">
                                                    {detail.label}
                                                </dt>
                                                <dd className="text-[11px] font-medium text-gray-800 dark:text-gray-200 break-words">
                                                    {detail.value}
                                                </dd>
                                            </React.Fragment>
                                        ))}
                                    </dl>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    // Notes filed before the per-action forms carry no answers,
                    // only the summary strings.
                    <div className="flex flex-wrap gap-2">
                        {note.types.map((type, index) => (
                            <span
                                key={index}
                                className="text-[12px] font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 px-3 py-1.5 rounded-lg"
                            >
                                {type}
                            </span>
                        ))}
                    </div>
                )}
            </Modal>
            {/* Full-size photo over the note, instead of a new tab. */}
            <DocumentViewer file={photoView} onClose={() => setPhotoView(null)} />
        </>
    );
};

const CareNotesTab = ({ residenceId }) => {
    const [careNotes, setCareNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCareNotes = async () => {
            try {
                setLoading(true);
                const response = await residencesService.getResidenceCareNotes(residenceId);
                setCareNotes(response.data);
                setError(null);
            } catch (err) {
                console.error('Error fetching care notes:', err);
                setError('Failed to load care notes');
            } finally {
                setLoading(false);
            }
        };

        if (residenceId) {
            fetchCareNotes();
        }
    }, [residenceId]);

    if (loading) {
        return <TabSkeleton />;
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <p className="text-red-500 text-sm">{error}</p>
            </div>
        );
    }

    return (
        <div className="mt-6 animate-in fade-in duration-300 w-full">

            {/* Header Section */}
            <div className="mb-6">
                <h2 className="text-[18px] font-semibold text-gray-800 dark:text-gray-100">Care notes</h2>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1">
                    Summary of care activities and observations recorded by staff members.
                </p>
            </div>

            {/* Main Content Area */}
            <div className="w-full flex flex-col">

                {/* Table Header */}
                <div
                    className="grid items-center px-6 py-2 text-[12px] font-medium text-gray-600 dark:text-gray-400 mb-2 gap-4"
                    style={{ gridTemplateColumns: '1.2fr 1fr 2.5fr 1.5fr 1fr 1fr' }}
                >
                    <div>Staff name</div>
                    <div>Date</div>
                    <div className="text-blue-600 dark:text-blue-400 underline cursor-pointer hover:text-blue-800 dark:hover:text-blue-300 transition">Description</div>
                    <div>Type</div>
                    <div>Priority</div>
                    <div className="text-center">Emotion</div>
                </div>

                {/* Table Rows (Card Style) */}
                <div className="flex flex-col gap-3">
                    {careNotes.length === 0 ? (
                        <div className="text-center py-12 bg-white dark:bg-[#262b33] rounded-xl border border-gray-100 dark:border-gray-800">
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No care notes found for this resident.</p>
                        </div>
                    ) : (
                        careNotes.map((note) => (
                            <CareNoteRow key={note.id} note={note} />
                        ))
                    )}
                </div>

            </div>
        </div>
    );
};

export default CareNotesTab;